import type { Node } from '@xyflow/react';
import {
  getAncestorPath,
  getVisibleDescendantIds,
  type LayoutResult,
} from './layout-mindmap';
import { DEPTH_COLORS } from './mindmap-constants';
import type { MindmapTreeNode, ParsedMindmap } from './mindmap-types';

export function getParsedMindmapNodeCount(parsed: ParsedMindmap | null): number {
  if (!parsed) {
    return 0;
  }

  return countMindmapNodes(parsed.root);
}

export function countMindmapNodes(root: MindmapTreeNode): number {
  return 1 + root.children.reduce((sum, child) => {
    return sum + countMindmapNodes(child);
  }, 0);
}

export function getTreeMaxDepth(root: MindmapTreeNode): number {
  if (root.children.length === 0) {
    return root.depth;
  }

  return root.children.reduce((maxDepth, child) => {
    return Math.max(maxDepth, getTreeMaxDepth(child));
  }, root.depth);
}

export function getMindmapMinimapNodeColor(node: Node): string {
  const colorIndex = typeof node.data?.colorIndex === 'number'
    ? node.data.colorIndex
    : 0;
  const baseColor = DEPTH_COLORS[colorIndex % DEPTH_COLORS.length] ?? DEPTH_COLORS[0];
  return `${baseColor}66`;
}

export function getVisibleNodeIds(layout: LayoutResult | null): Set<string> {
  if (!layout) {
    return new Set<string>();
  }

  return new Set(layout.nodes.map((node) => node.id));
}

export function getVisibleNodeYById(layout: LayoutResult | null): Map<string, number> {
  if (!layout) {
    return new Map<string, number>();
  }

  return layout.nodes.reduce((acc, node) => {
    const nodeHeight = typeof node.style?.height === 'number' ? node.style.height : 0;
    acc.set(node.id, node.position.y + nodeHeight / 2);
    return acc;
  }, new Map<string, number>());
}

export function getHighlightedMindmapNodeIds(input: {
  expandedNodeIds: Set<string>;
  hoveredNodeId: string | null;
  parentMap: Map<string, string>;
  root: MindmapTreeNode | null;
}): Set<string> {
  if (!input.hoveredNodeId || !input.root) {
    return new Set<string>();
  }

  const ancestors = getAncestorPath({
    nodeId: input.hoveredNodeId,
    parentMap: input.parentMap,
  });
  const descendants = getVisibleDescendantIds({
    root: input.root,
    nodeId: input.hoveredNodeId,
    expandedNodeIds: input.expandedNodeIds,
  });

  return new Set<string>([
    ...ancestors,
    ...Array.from(descendants),
  ]);
}
